import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  Divider,
  IconButton,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { selectUser, selectIsLoggedIn, selectUserName } from "../../store/userSlice";
import { 
  selectAdminClubIds, 
  selectPermissionsLoaded, 
  selectPermissionsLoading,
  fetchUserPermissions 
} from "../../store/permissionsSlice";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ClubPortalModal from "../ClubPortalModal";
import MKButton from "../MKButton";

function Sidebar({ 
  onLogout,
  onOpenSignIn,
  onOpenSignUp,
  onOpenSettings 
}) {
  const user = useAppSelector(selectUser);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const userName = useAppSelector(selectUserName);
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState(true);
  const [showClubPortal, setShowClubPortal] = useState(false);
  const navigate = useNavigate();
  
  // Get permissions from Redux
  const adminClubIds = useAppSelector(selectAdminClubIds);
  const permissionsLoaded = useAppSelector(selectPermissionsLoaded);
  const permissionsLoading = useAppSelector(selectPermissionsLoading);

  useEffect(() => {
    if (isLoggedIn && user?.id && !permissionsLoaded && !permissionsLoading) {
      dispatch(fetchUserPermissions(user.id));
    }
  }, [isLoggedIn, user?.id, dispatch]);

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };



  const drawerWidth = collapsed ? 64 : 240;

  const renderUserSection = () => (
    <Box sx={{ p: 2, textAlign: "center" }}>
      <Avatar
        sx={{ 
          width: collapsed ? 30 : 80, 
          height: collapsed ? 30 : 80, 
          mx: "auto", 
          mb: collapsed ? 0 : 1 
        }}
        src={user?.avatar_url}
      >
        {userName.charAt(0)}
      </Avatar>
      {!collapsed && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {userName}
        </Typography>
      )}
    </Box>
  );

  const renderAuthSection = () => (
    <List>
      <ListItem disablePadding>
        <ListItemButton onClick={onOpenSignIn}>
          <ListItemIcon>
            <LoginIcon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Sign In" />}
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={onOpenSignUp}>
          <ListItemIcon>
            <PersonAddIcon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Sign Up" />}
        </ListItemButton>
      </ListItem>
    </List>
  );

  const renderUserMenu = () => (
    <List>
      <ListItem disablePadding>
        <ListItemButton component={RouterLink} to="/dashboard">
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Dashboard" />}
        </ListItemButton>
      </ListItem>

      {/* Club Portal link, only for club owner or club admin */}
      {adminClubIds.length > 0 && (
        <ListItem disablePadding>
          <ListItemButton onClick={() => setShowClubPortal(true)}>
            <ListItemIcon>
              <GroupIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Club Portal" />}
          </ListItemButton>
        </ListItem>
      )}
      {/* Admin Console link, only for admin users */}
      {user?.role?.name === 'admin' && (
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin">
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Admin Console" />}
          </ListItemButton>
        </ListItem>
      )}
      <ListItem disablePadding>
        <ListItemButton onClick={onOpenSettings}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Settings" />}
        </ListItemButton>
      </ListItem>
      <Divider sx={{ my: 1 }} />
      <ListItem disablePadding>
        <ListItemButton onClick={onLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Logout" />}
        </ListItemButton>
      </ListItem>
    </List>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          borderRight: "1px solid rgba(0, 0, 0, 0.12)",
          transition: "width 0.2s ease-in-out",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", p: 1 }}>
        <IconButton 
          onClick={handleToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
      
      {isLoggedIn ? (
        <>
          {renderUserSection()}
          <Divider />
          {renderUserMenu()}
          <ClubPortalModal
            open={showClubPortal}
            onClose={() => setShowClubPortal(false)}
            adminClubIds={adminClubIds}
            onEditClub={clubId => {
              setShowClubPortal(false);
              navigate(`/clubs/${clubId}/edit`);
            }}
          />
        </>
      ) : (
        renderAuthSection()
      )}
    </Drawer>
  );
}

Sidebar.propTypes = {
  onLogout: PropTypes.func.isRequired,
  onOpenSignIn: PropTypes.func.isRequired,
  onOpenSignUp: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func.isRequired,
};

export default Sidebar; 